import React, { ReactNode } from 'react';
import styled from 'styled-components';

interface StunningIconWrapperProps {
  children: ReactNode;
  type?: 'social' | 'skill';
  platform?: string;
  delay?: number;
}

const IconContainer = styled.div<{ $type: string; $platform: string; $delay: number }>`
  height: 100px;
  width: 100px;
  border-radius: 16px;
  padding: 10px;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
  
  /* Dynamic gradient based on platform */
  background: ${props => getGradientForPlatform(props.$platform)};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  
  /* Animated gradient border */
  border: 2px solid transparent;
  box-shadow: 
    0 8px 24px ${props => getGlowColor(props.$platform)},
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  
  /* Floating animation */
  animation: float 3s ease-in-out infinite;
  animation-delay: ${props => props.$delay * 0.15}s;
  
  /* Shine effect overlay */
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    left: -50%;
    width: 200%;
    height: 200%;
    background: linear-gradient(
      45deg,
      transparent 30%,
      rgba(255, 255, 255, 0.2) 50%,
      transparent 70%
    );
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
    transition: transform 0.6s ease;
  }
  
  /* Gradient border reveal */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 16px;
    padding: 2px;
    background: ${props => getBorderGradient(props.$platform)};
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.5s ease;
  }
  
  /* Hover effects */
  &:hover {
    transform: translateY(-12px) scale(1.12) rotateY(8deg) rotateX(5deg);
    box-shadow: 
      0 20px 50px ${props => getGlowColor(props.$platform)},
      0 0 40px ${props => getGlowColor(props.$platform)},
      inset 0 2px 0 rgba(255, 255, 255, 0.2);
    
    &::before {
      transform: translateX(100%) translateY(100%) rotate(45deg);
    }
    
    &::after {
      opacity: 1;
    }
    
    img {
      transform: scale(1.15) rotateZ(8deg);
      filter: drop-shadow(0 0 15px rgba(255, 255, 255, 0.8)) brightness(1.2);
    }
  }
  
  /* Active state */
  &:active {
    transform: translateY(-8px) scale(1.08);
  }
  
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    position: relative;
    z-index: 1;
    transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
    filter: drop-shadow(0 4px 12px rgba(0, 0, 0, 0.3));
  }
  
  @keyframes float {
    0%, 100% {
      transform: translateY(0) rotateZ(0deg);
    }
    50% {
      transform: translateY(-10px) rotateZ(3deg);
    }
  }
  
  /* Mobile optimization */
  @media (max-width: 768px) {
    height: 85px;
    width: 85px;
    
    &:hover {
      transform: translateY(-8px) scale(1.08);
    }
  }
  
  @media (max-width: 480px) {
    height: 75px;
    width: 75px;
  }
  
  /* Accessibility */
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    
    &:hover {
      transform: scale(1.05);
    }
  }
`;

// Platform-specific gradients
const getGradientForPlatform = (platform: string): string => {
  const gradients: Record<string, string> = {
    // Social Media
    linkedin: 'linear-gradient(135deg, rgba(10, 102, 194, 0.9) 0%, rgba(0, 119, 181, 0.85) 100%)',
    github: 'linear-gradient(135deg, rgba(36, 41, 46, 0.95) 0%, rgba(22, 27, 34, 0.9) 100%)',
    kakao: 'linear-gradient(135deg, rgba(255, 235, 0, 0.9) 0%, rgba(250, 217, 0, 0.85) 100%)',
    kaggle: 'linear-gradient(135deg, rgba(32, 201, 255, 0.9) 0%, rgba(79, 195, 247, 0.85) 100%)',
    instagram: 'linear-gradient(135deg, rgba(225, 48, 108, 0.9) 0%, rgba(193, 53, 132, 0.85) 50%, rgba(131, 58, 180, 0.9) 100%)',
    telegram: 'linear-gradient(135deg, rgba(37, 150, 190, 0.9) 0%, rgba(34, 139, 230, 0.85) 100%)',
    
    // Skills
    pytorch: 'linear-gradient(135deg, rgba(238, 76, 44, 0.9) 0%, rgba(220, 65, 38, 0.85) 100%)',
    tensorflow: 'linear-gradient(135deg, rgba(255, 112, 67, 0.9) 0%, rgba(255, 91, 0, 0.85) 100%)',
    python: 'linear-gradient(135deg, rgba(55, 118, 171, 0.9) 0%, rgba(43, 93, 135, 0.85) 100%)',
    docker: 'linear-gradient(135deg, rgba(19, 113, 204, 0.9) 0%, rgba(9, 93, 176, 0.85) 100%)',
    lightning: 'linear-gradient(135deg, rgba(117, 79, 254, 0.9) 0%, rgba(95, 58, 235, 0.85) 100%)',
    nodejs: 'linear-gradient(135deg, rgba(104, 160, 99, 0.9) 0%, rgba(83, 130, 78, 0.85) 100%)',
    django: 'linear-gradient(135deg, rgba(9, 46, 32, 0.95) 0%, rgba(12, 75, 51, 0.9) 100%)',
    huggingface: 'linear-gradient(135deg, rgba(255, 213, 79, 0.9) 0%, rgba(255, 193, 7, 0.85) 100%)',
    numpy: 'linear-gradient(135deg, rgba(75, 130, 192, 0.9) 0%, rgba(51, 102, 153, 0.85) 100%)',
    opencv: 'linear-gradient(135deg, rgba(92, 207, 230, 0.9) 0%, rgba(0, 153, 204, 0.85) 100%)',
    sklearn: 'linear-gradient(135deg, rgba(242, 145, 59, 0.9) 0%, rgba(233, 122, 28, 0.85) 100%)',
    git: 'linear-gradient(135deg, rgba(240, 80, 50, 0.9) 0%, rgba(222, 68, 40, 0.85) 100%)',
    
    // Default
    default: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.12) 100%)',
  };
  
  return gradients[platform.toLowerCase()] || gradients.default;
};

// Platform-specific glow colors
const getGlowColor = (platform: string): string => {
  const glows: Record<string, string> = {
    linkedin: 'rgba(10, 102, 194, 0.4)',
    github: 'rgba(36, 41, 46, 0.5)',
    kakao: 'rgba(255, 235, 0, 0.4)',
    kaggle: 'rgba(32, 201, 255, 0.4)',
    instagram: 'rgba(225, 48, 108, 0.4)',
    telegram: 'rgba(37, 150, 190, 0.4)',
    pytorch: 'rgba(238, 76, 44, 0.4)',
    tensorflow: 'rgba(255, 112, 67, 0.4)',
    python: 'rgba(55, 118, 171, 0.4)',
    docker: 'rgba(19, 113, 204, 0.4)',
    lightning: 'rgba(117, 79, 254, 0.4)',
    nodejs: 'rgba(104, 160, 99, 0.4)',
    django: 'rgba(12, 75, 51, 0.5)',
    huggingface: 'rgba(255, 213, 79, 0.4)',
    numpy: 'rgba(75, 130, 192, 0.4)',
    opencv: 'rgba(92, 207, 230, 0.4)',
    sklearn: 'rgba(242, 145, 59, 0.4)',
    git: 'rgba(240, 80, 50, 0.4)',
    default: 'rgba(99, 102, 241, 0.4)',
  };
  
  return glows[platform.toLowerCase()] || glows.default;
};

// Platform-specific border gradients
const getBorderGradient = (platform: string): string => {
  const borders: Record<string, string> = {
    linkedin: 'linear-gradient(135deg, #0a66c2, #0077b5, #00a0dc)',
    github: 'linear-gradient(135deg, #24292e, #ffffff, #24292e)',
    kakao: 'linear-gradient(135deg, #ffe500, #ffd900, #ffb700)',
    kaggle: 'linear-gradient(135deg, #20c9ff, #4fc3f7, #00b8d4)',
    instagram: 'linear-gradient(135deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
    telegram: 'linear-gradient(135deg, #2596be, #229ed9, #1e88e5)',
    pytorch: 'linear-gradient(135deg, #ee4c2c, #dc4126, #c83820)',
    tensorflow: 'linear-gradient(135deg, #ff7043, #ff5b00, #ff6f00)',
    python: 'linear-gradient(135deg, #3776ab, #ffd343, #3776ab)',
    docker: 'linear-gradient(135deg, #1371cc, #095db0, #0288d1)',
    lightning: 'linear-gradient(135deg, #754ffe, #5f3aeb, #4a26da)',
    nodejs: 'linear-gradient(135deg, #68a063, #53824e, #417e38)',
    django: 'linear-gradient(135deg, #092e20, #0c4b33, #0d5c3a)',
    huggingface: 'linear-gradient(135deg, #ffd54f, #ffc107, #ffb300)',
    numpy: 'linear-gradient(135deg, #4b82c0, #336699, #2e5988)',
    opencv: 'linear-gradient(135deg, #5ccfe6, #0099cc, #0088bb)',
    sklearn: 'linear-gradient(135deg, #f2913b, #e97a1c, #d9681a)',
    git: 'linear-gradient(135deg, #f05032, #de4428, #c8381e)',
    default: 'linear-gradient(135deg, #6366f1, #8b5cf6, #06b6d4)',
  };
  
  return borders[platform.toLowerCase()] || borders.default;
};

export const StunningIconWrapper: React.FC<StunningIconWrapperProps> = ({ 
  children, 
  type = 'social', 
  platform = 'default',
  delay = 0 
}) => {
  return (
    <IconContainer $type={type} $platform={platform} $delay={delay}>
      {children}
    </IconContainer>
  );
};

